import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Phone, MapPin, Clock, Package } from 'lucide-react';

const STATUS_LABELS = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  preparing: { label: 'Em Preparo', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  delivering: { label: 'Saiu para Entrega', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  delivered: { label: 'Entregue', color: 'bg-green-100 text-green-800 border-green-300' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800 border-red-300' },
};

const AdminOrders = () => {
  const { authAxios } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000); // Auto refresh every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await authAxios.get('/admin/orders');
      setOrders(response.data);
    } catch (err) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await authAxios.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success('Status atualizado!');
      fetchOrders();
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const filteredOrders = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (loading) {
    return <div className="text-center py-10 text-[#5C4247]">Carregando pedidos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="text-2xl font-bold text-[#291014]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="orders-section-title"
        >
          Pedidos ({filteredOrders.length})
        </h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="orders-filter-select">
            <SelectValue placeholder="Filtrar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="confirmed">Confirmados</SelectItem>
            <SelectItem value="preparing">Em Preparo</SelectItem>
            <SelectItem value="delivering">Saiu para Entrega</SelectItem>
            <SelectItem value="delivered">Entregues</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-[#F3E8E3] text-center" data-testid="no-orders-message">
          <Package className="h-16 w-16 text-[#5C4247] mx-auto mb-4 opacity-50" />
          <p className="text-[#5C4247]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Nenhum pedido {filter !== 'all' ? `com status "${STATUS_LABELS[filter]?.label}"` : ''}.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredOrders.map((order) => {
            const statusInfo = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
            return (
              <div
                key={order.id}
                className="bg-white rounded-2xl p-5 border border-[#F3E8E3] hover:shadow-md transition-all"
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div>
                    <h3
                      className="font-bold text-[#291014]"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                      data-testid={`order-customer-${order.id}`}
                    >
                      {order.customer_name}
                    </h3>
                    <p className="text-xs text-[#5C4247] flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" /> {formatDate(order.created_at)}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold border ${statusInfo.color}`}
                    data-testid={`order-status-badge-${order.id}`}
                  >
                    {statusInfo.label}
                  </span>
                </div>

                <div className="space-y-1 mb-3 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <a
                    href={`https://wa.me/${order.customer_phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#291014] hover:text-[#7F1D1D]"
                  >
                    <Phone className="h-4 w-4 text-[#EA580C]" />
                    {order.customer_phone}
                  </a>
                  <div className="flex items-start gap-2 text-[#291014]">
                    <MapPin className="h-4 w-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
                    <span>
                      {order.delivery_address}
                      {order.delivery_zone && (
                        <span className="text-[#5C4247] block text-xs">Zona: {order.delivery_zone}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-[#FAF8F5] rounded-lg p-3 mb-3 text-sm">
                  <p className="font-semibold mb-2 text-[#291014]" style={{ fontFamily: 'Outfit, sans-serif' }}>Itens:</p>
                  <ul className="space-y-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {order.items.map((item, idx) => (
                      <li key={idx} className="flex justify-between text-[#291014]">
                        <span>
                          {item.quantity}x {item.product_name}
                        </span>
                        <span className="font-semibold">R$ {(item.price * item.quantity).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  {order.delivery_fee > 0 && (
                    <div className="flex justify-between mt-2 pt-2 border-t border-[#F3E8E3] text-[#5C4247]">
                      <span>Taxa de entrega</span>
                      <span>R$ {order.delivery_fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t border-[#F3E8E3] font-bold text-[#7F1D1D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <span>Total</span>
                    <span data-testid={`order-total-${order.id}`}>R$ {order.total.toFixed(2)}</span>
                  </div>
                </div>

                {order.notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3 text-xs text-yellow-800">
                    <strong>Obs:</strong> {order.notes}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 text-xs">
                  <span
                    className="text-[#5C4247] uppercase font-semibold"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    💰 {order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'dinheiro' ? 'Dinheiro' : 'Cartão'}
                  </span>
                </div>

                <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                  <SelectTrigger className="mt-3 w-full" data-testid={`order-status-select-${order.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
